import { acceptHMRUpdate, defineStore } from 'pinia';
import { http as axios } from '@/services/http.service';

export interface Specialty {
  id?: number;
  name: string;
  duration_blocks: number;
  description?: string | null;
  price?: number;
  block_size_minutes?: number | null;
}

interface SpecialtiesState {
  items: Specialty[];
  loading: boolean;
  error: string;
}

// Apex brand-scoped base path when a tenant is given; otherwise the unqualified
// path the brand's own host (and the website) already resolve against the active
// schema.
const basePath = (tenantId?: number | null) =>
  tenantId ? `/api/v1/tenants/${tenantId}/specialties` : '/api/v1/specialties';

export const useSpecialtiesStore = defineStore('specialties.store', {
  state: (): SpecialtiesState => ({ items: [], loading: false, error: '' }),
  actions: {
    async fetchAll(tenantId?: number | null): Promise<Specialty[]> {
      this.loading = true;
      this.error = '';
      try {
        const res = await axios.get(basePath(tenantId));
        this.items = Array.isArray(res.data) ? res.data : [];
        return this.items;
      } catch (e: unknown) {
        const message =
          typeof e === 'object' && e && 'message' in e
            ? String((e as { message?: unknown }).message)
            : '';
        this.error = message || 'Error cargando especialidades';
        this.items = [];
        return [];
      } finally {
        this.loading = false;
      }
    },
    async create(data: Specialty, tenantId?: number | null): Promise<Specialty | null> {
      const res = await axios.post(basePath(tenantId), { specialty: data });
      const item = res.data as Specialty;
      this.items.push(item);
      return item;
    },
    async update(
      id: number,
      data: Partial<Specialty>,
      tenantId?: number | null,
    ): Promise<Specialty | null> {
      const res = await axios.put(`${basePath(tenantId)}/${id}`, { specialty: data });
      const updated = res.data as Specialty;
      const idx = this.items.findIndex((i) => i.id === id);
      if (idx !== -1) this.items[idx] = updated;
      return updated;
    },
    async remove(id: number, tenantId?: number | null): Promise<void> {
      await axios.delete(`${basePath(tenantId)}/${id}`);
      this.items = this.items.filter((i) => i.id !== id);
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSpecialtiesStore, import.meta.hot));
}
